import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getStockSnapshots } from "../marketData/marketDataApi";
import "./NinaDayScanner.css";

const REFRESH_MS = 15000;
const EXTENDED_FEED = "delayed_sip";
const DEFAULT_UNIVERSE = [
  "PLUG", "AAPL", "NVDA", "AMD", "TSLA", "AMZN", "MSFT", "META",
  "GOOGL", "NFLX", "AVGO", "INTC", "SOFI", "HOOD", "COIN", "SMCI",
  "ARM", "PLTR", "RIVN", "LCID", "F", "NIO", "BAC", "JPM", "XOM",
  "CVX", "UBER", "SPY", "QQQ", "IWM", "SLV", "RXT",
];

function finiteNumber(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function firstFinite(...values) {
  for (const value of values) {
    const normalized = finiteNumber(value);
    if (normalized !== null) return normalized;
  }
  return null;
}

function formatPrice(value) {
  const number = finiteNumber(value);
  return number === null ? "—" : `$${number.toFixed(2)}`;
}

function formatPercent(value) {
  const number = finiteNumber(value);
  if (number === null) return "—";
  return `${number >= 0 ? "+" : ""}${number.toFixed(2)}%`;
}

function formatVolume(value) {
  const number = finiteNumber(value);
  if (number === null) return "—";
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(number);
}

function formatTimestamp(value) {
  if (!value) return "Update unavailable";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Update unavailable";
  return parsed.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    timeZone: "America/New_York",
    timeZoneName: "short",
  });
}

function easternClockParts(value) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;

  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(parsed);
  const get = (type) => parts.find((part) => part.type === type)?.value;
  const hour = Number(get("hour"));
  const minute = Number(get("minute"));
  const weekday = get("weekday");

  if (!Number.isInteger(hour) || !Number.isInteger(minute) || !weekday) return null;
  return { hour, minute, weekday };
}

function isPremarketTimestamp(value) {
  const parts = easternClockParts(value);
  if (!parts || parts.weekday === "Sat" || parts.weekday === "Sun") return false;
  const totalMinutes = parts.hour * 60 + parts.minute;
  return totalMinutes >= 4 * 60 && totalMinutes < 9 * 60 + 30;
}

function currentSelectedSymbol() {
  try {
    return String(localStorage.getItem("aninaSelectedSymbol") || "PLUG")
      .trim()
      .toUpperCase();
  } catch (error) {
    return "PLUG";
  }
}

function scannerUniverse() {
  const selected = currentSelectedSymbol();
  return Array.from(new Set([selected, ...DEFAULT_UNIVERSE]));
}

function parseTimestamp(value) {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function freshestMarketEvent(candidates) {
  return candidates.reduce((freshest, candidate) => {
    const timestamp = parseTimestamp(candidate?.timestamp);
    if (!timestamp) return freshest;
    if (!freshest || timestamp > freshest.timestamp) {
      return { ...candidate, timestamp };
    }
    return freshest;
  }, null);
}

function normalizeSnapshot(symbol, snapshot) {
  const latestTrade = snapshot?.latest_trade || {};
  const latestQuote = snapshot?.latest_quote || {};
  const minuteBar = snapshot?.minute_bar || {};
  const dailyBar = snapshot?.daily_bar || {};
  const previousDailyBar = snapshot?.previous_daily_bar || {};
  const source = snapshot?.source || {};
  const freshest = freshestMarketEvent([
    { timestamp: latestTrade.t, price: finiteNumber(latestTrade.p), kind: "trade" },
    { timestamp: minuteBar.t, price: finiteNumber(minuteBar.c), kind: "minute-bar" },
    { timestamp: latestQuote.t, price: null, kind: "quote" },
  ]);
  const updatedAt = freshest?.timestamp?.toISOString() || dailyBar.t || source.last_update_at || null;
  const lastPrice = freshest?.price ?? firstFinite(latestTrade.p, minuteBar.c);
  const previousClose = finiteNumber(previousDailyBar.c);
  const dollarChange =
    lastPrice !== null && previousClose !== null ? lastPrice - previousClose : null;
  const percentageChange =
    dollarChange !== null && previousClose > 0
      ? (dollarChange / previousClose) * 100
      : null;

  return {
    symbol,
    lastPrice,
    previousClose,
    percentageChange,
    bid: finiteNumber(latestQuote.bp),
    ask: finiteNumber(latestQuote.ap),
    high: finiteNumber(dailyBar.h),
    low: finiteNumber(dailyBar.l),
    volume: finiteNumber(dailyBar.v),
    vwap: firstFinite(dailyBar.vw, minuteBar.vw),
    updatedAt,
    isPremarket: isPremarketTimestamp(updatedAt),
    provider: source.provider || "Alpaca",
    feed: source.feed || "",
    quality: source.quality_level || source.quality_state || "Unknown",
    notice: source.nina_data_quality_notice || "",
  };
}

export default function NinaLivePremarketScanner({ onOpenTradeReview }) {
  const symbols = useMemo(() => scannerUniverse(), []);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("loading");
  const [errors, setErrors] = useState([]);
  const [lastRefreshAt, setLastRefreshAt] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const loadSnapshots = useCallback(async ({ forceRefresh = false } = {}) => {
    if (forceRefresh) setIsRefreshing(true);

    try {
      const batch = await getStockSnapshots(
        symbols,
        EXTENDED_FEED,
        forceRefresh ? { cacheMode: "reload" } : {}
      );
      const authenticatedRows = [];
      const nextErrors = [];
      const snapshots = batch?.snapshots || {};
      const missingSymbols = new Set(batch?.missing_symbols || []);

      symbols.forEach((symbol) => {
        const snapshot = snapshots[symbol];
        if (snapshot) {
          const row = normalizeSnapshot(symbol, snapshot);
          if (row.isPremarket) authenticatedRows.push(row);
          return;
        }
        if (missingSymbols.has(symbol)) {
          nextErrors.push({
            symbol,
            message: "No authenticated Alpaca snapshot was returned for this symbol.",
          });
        }
      });

      authenticatedRows.sort((a, b) => {
        const aChange = finiteNumber(a.percentageChange) ?? -Infinity;
        const bChange = finiteNumber(b.percentageChange) ?? -Infinity;
        return bChange - aChange || a.symbol.localeCompare(b.symbol);
      });

      setRows(authenticatedRows);
      setErrors(nextErrors);
      setLastRefreshAt(new Date());
      setStatus(authenticatedRows.length ? "ready" : "empty");
    } catch (error) {
      setErrors([
        {
          symbol: "BATCH",
          message: error?.message || "Authenticated Alpaca market data is unavailable.",
        },
      ]);
      setRows([]);
      setLastRefreshAt(new Date());
      setStatus("empty");
    } finally {
      if (forceRefresh) setIsRefreshing(false);
    }
  }, [symbols]);

  useEffect(() => {
    let active = true;
    const run = async () => {
      if (active) await loadSnapshots();
    };
    run();
    const timerId = window.setInterval(run, REFRESH_MS);
    return () => {
      active = false;
      window.clearInterval(timerId);
    };
  }, [loadSnapshots]);

  const openSymbol = (row) => {
    try {
      localStorage.setItem("aninaSelectedSymbol", row.symbol);
    } catch (error) {
      // The in-session event below still updates Live Trading.
    }

    window.dispatchEvent(
      new CustomEvent("nina:symbol-change", { detail: { symbol: row.symbol } })
    );

    if (typeof onOpenTradeReview === "function") {
      onOpenTradeReview({
        symbol: row.symbol,
        source: "alpaca-live-premarket-scanner",
        evidenceStatus: "provider-live",
        currentPrice: row.lastPrice,
        provider: row.provider,
        feed: row.feed,
        dataQuality: row.quality,
        reviewOnly: true,
      });
    }
  };

  return (
    <section className="ninaScannerPage" aria-labelledby="live-premarket-scanner-title">
      <header className="scannerHero scannerCompactPanel">
        <span className="scannerEyebrow">AUTHENTICATED EXTENDED-HOURS DATA</span>
        <h1 id="live-premarket-scanner-title">🌅 AI Premarket Scanner</h1>
        <p>
          Premarket rows come only from authenticated Alpaca delayed-SIP snapshots whose freshest
          trade, minute-bar, or quote timestamp falls between 4:00 AM and 9:30 AM New York time.
        </p>
        <div className="scannerDemoNotice" role="note">
          <strong>Authenticated premarket prices only</strong>
          <span>
            Provider, feed, data quality, timestamp, quote, price, volume, and VWAP remain
            source-labeled. Extended-hours evidence uses consolidated SIP data delayed by about
            15 minutes rather than IEX-only coverage.
          </span>
        </div>
      </header>

      <section className="scannerCompactPanel" aria-label="Premarket scanner data status">
        <div className="scannerStatusRow">
          <strong>
            {status === "loading"
              ? "Loading authenticated premarket snapshots…"
              : status === "ready"
              ? `${rows.length} authenticated premarket symbols loaded`
              : "No authenticated premarket rows in the current snapshot set"}
          </strong>
          <span>
            {lastRefreshAt
              ? `Last refresh ${formatTimestamp(lastRefreshAt.toISOString())}`
              : "Waiting for first refresh"}
          </span>
          <button
            type="button"
            onClick={() => loadSnapshots({ forceRefresh: true })}
            disabled={status === "loading" || isRefreshing}
          >
            {isRefreshing ? "Refreshing…" : "Refresh now"}
          </button>
        </div>

        {errors.length > 0 && (
          <div className="scannerDemoNotice" role="status">
            <strong>Partial provider coverage</strong>
            <span>{errors.map((error) => `${error.symbol}: ${error.message}`).join(" • ")}</span>
          </div>
        )}
      </section>

      <section className="scannerCompactPanel">
        <div className="scannerTableWrap">
          <table className="scannerTable">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Premarket Last</th>
                <th>Vs Prev Close</th>
                <th>Bid</th>
                <th>Ask</th>
                <th>Session High</th>
                <th>Session Low</th>
                <th>Volume</th>
                <th>VWAP</th>
                <th>Provider / Feed</th>
                <th>Quality</th>
                <th>Updated ET</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.symbol}>
                  <td>
                    <button
                      type="button"
                      className="scannerSymbolButton"
                      onClick={() => openSymbol(row)}
                      title={`Open ${row.symbol} in Live Trading`}
                    >
                      {row.symbol}
                    </button>
                  </td>
                  <td>{formatPrice(row.lastPrice)}</td>
                  <td className={finiteNumber(row.percentageChange) < 0 ? "negative" : "positive"}>
                    {formatPercent(row.percentageChange)}
                  </td>
                  <td>{formatPrice(row.bid)}</td>
                  <td>{formatPrice(row.ask)}</td>
                  <td>{formatPrice(row.high)}</td>
                  <td>{formatPrice(row.low)}</td>
                  <td>{formatVolume(row.volume)}</td>
                  <td>{formatPrice(row.vwap)}</td>
                  <td>{row.provider} {row.feed ? `· ${row.feed.toUpperCase()}` : ""}</td>
                  <td>{row.quality}</td>
                  <td>{formatTimestamp(row.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {status === "empty" && (
          <div className="scannerDemoNotice" role="status">
            <strong>No premarket rows passed the timestamp check.</strong>
            <span>
              AninaTrade will leave the table empty rather than reuse regular-session data or
              fabricate premarket values.
            </span>
          </div>
        )}
      </section>
    </section>
  );
}
